import { motion } from 'motion/react'
import { RevealItem, ScrollReveal } from '#/components/common/ScrollReveal'
import { TextAnimate } from '#/components/common/TextAnimate'
import { HighlightText } from '#/components/common/HighlightText'
import headerIcon from '#/assets/header-icon.svg'
import { TEACHERS_CONTENT } from '#/data/aboutTeachers'
import { useTeachers } from '#/features/teachers/hooks/useTeachers'
import { Avatar, AvatarImage, AvatarFallback } from '#/components/ui/avatar'
import NoData from '#/components/common/NoData'

import { getImageUrl } from '#/lib/imageUrl'

function getInitials(name: string): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function FloatingCrescent() {
  return (
    <motion.svg
      className="pointer-events-none absolute right-0 top-16 h-80 w-80 opacity-[0.08] max-xl:hidden"
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
      transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
    >
      <circle cx="100" cy="100" r="90" fill="#FFB606" />
      <circle cx="130" cy="80" r="72" fill="#f5f5f4" />
      {[
        { x: 155, y: 50 },
        { x: 172, y: 78 },
        { x: 145, y: 25 },
      ].map((star, i) => (
        <polygon
          key={i}
          points={`${star.x},${star.y - 8} ${star.x + 2.5},${star.y - 2} ${star.x + 9},${star.y - 2} ${star.x + 4},${star.y + 2.5} ${star.x + 6},${star.y + 9} ${star.x},${star.y + 5} ${star.x - 6},${star.y + 9} ${star.x - 4},${star.y + 2.5} ${star.x - 9},${star.y - 2} ${star.x - 2.5},${star.y - 2}`}
          fill="#FFB606"
        />
      ))}
    </motion.svg>
  )
}

function GeometricBg() {
  return (
    <div
      className="pointer-events-none absolute left-0 bottom-0 h-96 w-96 opacity-20 max-lg:hidden"
      aria-hidden="true"
    >
      <svg
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          animation: 'spin-slow 60s linear infinite',
          transformOrigin: '50% 50%',
        }}
        viewBox="0 0 400 400"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {[0, 22.5, 45, 67.5, 90, 112.5, 135, 157.5].map((deg) => (
          <rect
            key={deg}
            x="100"
            y="100"
            width="200"
            height="200"
            stroke="#FFB606"
            strokeWidth="1"
            fill="none"
            transform={`rotate(${deg} 200 200)`}
          />
        ))}
        <circle
          cx="200"
          cy="200"
          r="80"
          stroke="#FFB606"
          strokeWidth="1"
          fill="none"
        />
        <circle
          cx="200"
          cy="200"
          r="120"
          stroke="#FFB606"
          strokeWidth="0.5"
          fill="none"
        />
      </svg>
    </div>
  )
}

export default function TeachersSection() {
  const { data: teachersRes, isLoading } = useTeachers()
  const teachers = teachersRes?.data || []

  return (
    <section
      className="relative w-full py-20 sm:py-28 overflow-hidden bg-white scroll-mt-20"
      id="teachers"
    >
      <FloatingCrescent />
      <GeometricBg />

      <div className="container mx-auto flex flex-col gap-16">
        {/* Header */}
        <ScrollReveal className="flex flex-col items-center gap-4 text-center">
          <RevealItem>
            <img alt="" src={headerIcon} className="opacity-60" />
          </RevealItem>
          <RevealItem>
            <h3 className="text-xl font-bold leading-[normal] text-primary-900 text-center whitespace-nowrap max-sm:text-base max-sm:whitespace-normal">
              {TEACHERS_CONTENT.eyebrow}
            </h3>
          </RevealItem>
          <RevealItem className="w-full">
            <h2 className="text-4xl font-bold text-neutral-900 sm:text-5xl">
              {TEACHERS_CONTENT.title}{' '}
              <HighlightText inView text={TEACHERS_CONTENT.highlight} />
            </h2>
          </RevealItem>
          <RevealItem className="max-w-2xl">
            <TextAnimate
              by="word"
              animation="fadeIn"
              duration={1.1}
              className="text-base text-neutral-600 sm:text-lg"
            >
              {TEACHERS_CONTENT.description}
            </TextAnimate>
          </RevealItem>
        </ScrollReveal>

        {/* Teacher cards */}
        {isLoading ? (
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-80 w-full animate-pulse rounded-2xl bg-neutral-100"
              />
            ))}
          </div>
        ) : teachers.length === 0 ? (
          <NoData
            title="No Teachers Found"
            description="Our faculty list will be available soon."
          />
        ) : (
          <ScrollReveal
            className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-4"
            stagger={0.14}
          >
            {teachers.map((teacher) => {
              const specialties =
                teacher.specialties || teacher.specializations || []
              const avatar = getImageUrl(teacher.imageUrl || teacher.avatarUrl)
              const title = teacher.title || teacher.role || 'Instructor'

              return (
                <RevealItem key={teacher.id || teacher.name}>
                  <motion.div
                    whileHover={{ y: -8, transition: { duration: 0.3 } }}
                    className="group h-full relative flex flex-col items-center gap-5 rounded-2xl border border-neutral-200 bg-white p-6 text-center shadow-[0px_4px_20px_0px_rgba(106,155,145,0.16)] transition-all duration-300 hover:border-secondary-400/60"
                  >
                    <div
                      className="absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100 pointer-events-none"
                      style={{
                        boxShadow: 'inset 0 0 30px rgba(255,182,6,0.06)',
                      }}
                    />

                    {/* Avatar using Shadcn Avatar component */}
                    <div className="relative">
                      <Avatar className="size-28 border-2 border-secondary-400/40 transition-all duration-300 group-hover:border-secondary-500 group-hover:shadow-[0_0_20px_rgba(255,182,6,0.25)]">
                        <AvatarImage src={avatar} alt={teacher.name} />
                        <AvatarFallback className="text-2xl font-bold bg-primary-100 text-primary-800">
                          {getInitials(teacher.name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="absolute bottom-1 right-1 size-4 rounded-full bg-green-500 border-2 border-white block" />
                    </div>

                    {/* Name + title */}
                    <div className="flex flex-col gap-1">
                      <h3 className="text-lg font-bold text-neutral-900">
                        {teacher.name}
                      </h3>
                      <p className="text-sm font-semibold text-secondary-500">
                        {title}
                      </p>
                    </div>

                    {/* Bio */}
                    {teacher.bio && (
                      <p className="text-sm text-neutral-500 leading-relaxed line-clamp-3">
                        {teacher.bio}
                      </p>
                    )}

                    {/* Specialties / Specialization badges */}
                    {specialties.length > 0 && (
                      <div className="flex flex-wrap justify-center gap-2 mt-auto pt-2">
                        {specialties.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full border border-secondary-400/30 bg-secondary-50 px-3 py-1 text-xs font-medium text-secondary-600"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </motion.div>
                </RevealItem>
              )
            })}
          </ScrollReveal>
        )}
      </div>
    </section>
  )
}
